import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Lens, LENSES } from '../../lenses/lens';
import { ScopeService } from '../../analysis/scope';
import { UserService } from '../../user/user.service';
import { CURRENT_STATE_PROJECT_KEY, Linqset, LinqsetNode, MODEL_TYPE, NODE_TYPE, ORGANISATION_MODEL_KEY, TUTORIAL_MODEL_KEY } from '../../common/linqset-model';
import { Organisation, Project, ModelDetails, TagGroup, TAG_GROUP_ALL_MODELS, ORGANISATION_STATE, calculateRawCostsForOrganisation, getUnsortedModelList, MODEL_FUNCTION } from '../../common/organisation';
import { BILLING_URL, DEFAULT_URL, MODEL_WALKTHROUGH_URL, ONBOARDING_URL, OVERVIEW_URL, PROJECTS_URL, SKETCH_URL } from '../../app-urls';
import { getListOfChanges, getNumberOfChanges } from '../../common/linqset-change-model';
import { USER_ROLE } from '../../common/user';
import { GraphSearchNode } from '../search/search.component';
import { FUNCTION, FunctionService } from '../../model-services/function.service';
import { environment } from '../../../environments/environment';
import { NodeService } from '../../model-services/node.service';
import { ModelService } from '../../organisation/models/model-service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { SCREEN } from '../../layouts/layouts-enums';
import { LinqsetRepositoryService } from '../../model-services/linqset-repository.service';
import { HelpGuideSummary } from '../../common/help-model';
import { HelpGuidesService } from '../../help/guides/help-guides.service';
import { Subscription } from 'rxjs';
import { RepositoryService } from '../../linq-utils/repository.service';
import { HelpService } from '../../help/help-services';
import { OrganisationService } from '../../organisation/organisation.service';
import { IMAGE_LINQ } from '../../model-services/model-item-enums';
import { NodeStyleSettings } from '../svgs/node-svgs';
import { GroupItem, ItemSelection } from '../group-selection/group-selection.component';
import { TagsService } from '../../organisation/tags/tags-service';

interface MENU_ITEM {
    _key:string, name:string, route:string, model:ModelDetails, icon:string, role:USER_ROLE, sandbox:boolean, settings:NodeStyleSettings
}

@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
    standalone: false
})
export class MenuComponent  implements OnChanges, OnInit, OnDestroy {
    @Input() organisation:Organisation
    @Input() project:Project
    @Input() linqset:Linqset
    @Input() screen:SCREEN
    @Input() modelType:MODEL_TYPE
    @Input() helpGuides:string[]                         // the guides about this screen, offered first in the help menu
    @Input() searchNodes:GraphSearchNode[]|undefined     // the nodes the screen's own graph shows, for search to find

    IMAGE_LINQ = IMAGE_LINQ
    nodes:LinqsetNode[]

    modelsData:GroupItem[]
    stateName:string
    modelName:string

    models:ModelDetails[]
    modelFunctions:MODEL_FUNCTION[] = []
    modelTags:string[] = []

    functionMap:any
    FUNCTION = FUNCTION
    resizeSub:Subscription
    updatedOrganisationSub:Subscription

    isExpanded:boolean
    @Output() menuEvent = new EventEmitter<{function:FUNCTION, value?:any}>()
    @Output() selectNodeEvent = new EventEmitter<string>()
    @Output() modelFilterEvent = new EventEmitter<{functions:MODEL_FUNCTION[], tags:string[]}>()

    modelMenuItems:MENU_ITEM[] = []
    projectMenuItems:MENU_ITEM[] = []
    dropdown:boolean = false                // at a certain screen size we go to drop down menu
    smallScreen:boolean = false              // at a certain screen size below this we drop search and billing button
    ORGANISATION_STATE = ORGANISATION_STATE
    SCREEN = SCREEN
    NODE_TYPE = NODE_TYPE

    billingText:string
    breakPoint:string = "(min-width: 960px) and (max-width: 1400px)"

    constructor(private userService: UserService, private router: Router, private modelService:ModelService, private cdRef: ChangeDetectorRef, private helpService:HelpService, private guidesService:HelpGuidesService, private organisationService:OrganisationService,
        private functionService:FunctionService, private nodeService:NodeService, private responsive: BreakpointObserver, private repoService:LinqsetRepositoryService,
        private repositoryService: RepositoryService, private tagService:TagsService, private scopeService:ScopeService) {
    }

    ngOnDestroy(): void {
        if (this.resizeSub) this.resizeSub.unsubscribe()
        if (this.updatedOrganisationSub) this.updatedOrganisationSub.unsubscribe()
        this.scopeSub?.unsubscribe()
    }

    ngOnInit(): void {
        this.guidesService.guides().then(guides => this.allGuides = guides).catch(() => undefined)
        this.resizeSub = this.responsive.observe([Breakpoints.XSmall, Breakpoints.Small, this.breakPoint]).subscribe(result => {
            this.smallScreen = (result.breakpoints[Breakpoints.Small] || result.breakpoints[Breakpoints.XSmall])
            this.dropdown = result.matches
            this.cdRef.detectChanges()
        });

        this.updatedOrganisationSub = this.organisationService.updatedOrganisationEvent.subscribe(e => {
            this.setup()
        })

        // the analysis screens are of the workspace and models chosen there, so the bar shows those
        this.scopeSub = this.scopeService.scope$.subscribe(() => this.followScope())

        this.setup()
    }

    // on an analysis screen: the project being looked at, if one is, and the model if only one is
    scopeModelName?:string
    private scopeSub?:Subscription
    private followScope() {
        if (this.screen !== SCREEN.ANALYSIS || !this.organisation) return
        const scope = this.scopeService.scope
        const project = scope.workspaceKey !== CURRENT_STATE_PROJECT_KEY ? this.organisation.projects?.find(p => p._key === scope.workspaceKey) : undefined
        this.scopeModelName = scope.modelKeys.length === 1 ? this.organisation.models?.find(m => m._key === scope.modelKeys[0])?.name : undefined
        if (project !== this.project) {
            this.project = project
            this.setup()
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['organisation'] || changes['screen']) this.followScope()
        if (changes['organisation'] || changes['linqset'] || changes['project'])
            this.setup()
    }

    isModelScreen() {
        return this.screen === SCREEN.MODEL
    }

    isInsightsScreen() {
        return this.screen === SCREEN.INSIGHTS
    }

    hasTour() {
        return this.screen == SCREEN.MODEL || this.screen == SCREEN.INSIGHTS || this.screen == SCREEN.ANALYSIS
    }

    setup() {
        this.functionMap = this.functionService.getFunctionMap(false, this.linqset, this.project)

        this.modelName = this.linqset?this.linqset.model.name:"Select model"

        if (!this.organisation) return

        const models = getUnsortedModelList(this.organisation, this.project)

        this.modelMenuItems = models.sort((a,b) => a.name?.toLowerCase() > b.name?.toLowerCase()?1:-1).map(m => {
            const settings = this.nodeService.createModelNodeSettings(m, this.project?._key)

            return {
                _key: m._key,
                name: m.name,
                model:m,
                settings,
                role: m.role
            } as MENU_ITEM
        })
        // the other workspaces to switch to. A model created in a project only exists there, so it has none;
        // pages without a model (such as billing) still list them
        this.projectMenuItems = []
        const currentWorkspace = this.getCurrentWorkspaceKey()
        if (!this.linqset || this.linqset.model.projectKey === CURRENT_STATE_PROJECT_KEY) {
            this.projectMenuItems = (this.organisation.projects || []).filter(p => p._key !== currentWorkspace).sort((a,b) => a.name?.toLowerCase() > b.name?.toLowerCase()?1:-1).map(p => {
                return {
                    _key: p._key,
                    name: p.name,
                    role: p.role,
                    sandbox:p.sandbox
                } as MENU_ITEM
            })
            if (currentWorkspace !== CURRENT_STATE_PROJECT_KEY) {
                this.projectMenuItems.unshift(
                    {
                        _key: CURRENT_STATE_PROJECT_KEY,
                        name: "Current state",
                        role: this.organisation.role,
                        sandbox: false
                    } as MENU_ITEM
                )
            }
        }

        this.modelsData = []
        const modelFunctions = models.reduce((functions:MODEL_FUNCTION[], m) => {
            if (functions.indexOf(m.function) < 0) functions.push(m.function)
            return functions
        }, [])

        this.modelsData.push(new GroupItem("functions", undefined, "Model functions", undefined, false, undefined, modelFunctions.map(f => {return new GroupItem(f, "functions", f, undefined, this.modelFunctions.indexOf(f)>=0)})))
        if (this.organisation.modelTags.tags) this.modelsData.push(new GroupItem("tags", undefined, "Model tags", undefined, false, undefined, this.organisation.modelTags.tags.map(t => {return new GroupItem(t._key, "tags", t.name, this.tagService.getIconForTag(t), this.modelTags.indexOf(t._key)>=0)})))

        this.stateName = this.project?this.project.name:"Current state"
        this.billingText = this.getBillingText()
        this.cdRef.detectChanges()
    }

    getColour() {
        if (environment.production) return "primary"
        else return "accent"
    }

    getVersion() {
        return "Version: " + environment.version
    }

    onModelFilterOpened(event:{open:boolean, selection:ItemSelection[]}) {
        if (!event.open) {
            // closing
            this.modelFunctions = []
            this.modelTags = []

            event.selection.forEach(i => {
                switch (i.item.parentKey) {
                    case "functions":
                        this.modelFunctions.push(i.item._key as MODEL_FUNCTION)
                        break
                    case "tags":
                        this.modelTags.push(i.item._key)
                        break
                }
            })
            this.modelFilterEvent.emit({functions:this.modelFunctions, tags:this.modelTags})
        }
    }

    selectModel(model:ModelDetails) {
        this.modelService.editModel(this.organisation, model, this.project?._key||CURRENT_STATE_PROJECT_KEY)
    }

    // the workspace being shown: the model's, or the project passed in, or current state
    getCurrentWorkspaceKey() {
        return this.linqset?.workspaceKey || this.project?._key || CURRENT_STATE_PROJECT_KEY
    }

    selectWorkspace(workspaceKey?:string) {
        if (this.screen == SCREEN.ORGANISATION_HOME || !this.linqset || this.linqset.isFilterModel()) {
            this.router.navigateByUrl(PROJECTS_URL(this.organisation._key, workspaceKey||CURRENT_STATE_PROJECT_KEY))
        }else {
            this.modelService.editModel(this.organisation, this.linqset.model, workspaceKey||CURRENT_STATE_PROJECT_KEY)
        }
    }

    openTutorialModel() {
        this.router.navigateByUrl(SKETCH_URL(this.organisation._key, TUTORIAL_MODEL_KEY, MODEL_TYPE.PROCESS))
    }

    openHelpChat() {
        this.helpService.openHelpChat()
    }

    openOnboardingPanel() {
        this.helpService.openIntroductonDialog()
    }

    openWalkthrough() {
        this.router.navigateByUrl(ONBOARDING_URL(this.organisation && this.organisation._key))
    }

    canEditModelDetails() {
        if (!this.linqset) return false
        if (this.linqset.isOrganisationModel()) return false
        if (this.linqset.modelKey === TUTORIAL_MODEL_KEY) return false
        return !this.linqset.readonly
    }

    canEditProjectDetails() {
        if (!this.linqset) return false
        if (this.linqset.isCurrentState()) return false
        return (this.linqset.project && this.linqset.project.role !== USER_ROLE.READ)
    }

    isSysAdminUser() {
        try {
            return this.userService.isSysAdmin()
        }catch(err) {
            console.log(err)
            //this.router.navigateByUrl("auth")
            return false
        }
    }

    tuneAI() {
        this.repositoryService.callSingleAPI(this.repositoryService.getData("api/generateaidata"), "MENU", "Tune AI")
    }

    getBillingText() {
        const costs = calculateRawCostsForOrganisation(this.organisation)

        if (this.organisation.state === ORGANISATION_STATE.NEAR_EXPIRY) {
            return "Expiring"
        }else if (this.organisation.state === ORGANISATION_STATE.EXPIRED) {
            return "Expired"
        }else if (this.organisation.isExemptFromPayment) {
            return "Exempt"
        }else if (costs.amount === 0) {
            return "Free"
        }else {
            return "Paid"
        }
    }

    clickBillingButton() {
        this.router.navigateByUrl(BILLING_URL(this.organisation._key))
    }

    enableExtensionButton() {
        return this.organisation.state === ORGANISATION_STATE.NEAR_EXPIRY || this.organisation.state === ORGANISATION_STATE.EXPIRED
    }

    getBillingColour() {
        if (this.organisation.state === ORGANISATION_STATE.NEAR_EXPIRY) {
            return "background-color:orange;color:white;"
        }else if (this.organisation.state === ORGANISATION_STATE.EXPIRED) {
            return "background-color:red;color:white;"
        }else {
            return "background-color:green;color:white;"
        }
    }

    hasDeletedModels() {
        return this.organisation && this.organisation.models.find(m => m.deletedDate)
    }

    isOrgAdmin() {
        return this.organisation && this.organisation.role === USER_ROLE.ADMIN
    }

    private allGuides:HelpGuideSummary[] = []

    // the guides about this screen, once their list has come from the server
    get screenGuides():HelpGuideSummary[] {
        return (this.helpGuides || []).flatMap(slug => this.allGuides.filter(g => g.slug === slug))
    }

    openGuide(slug?:string) {
        this.guidesService.open(slug)
    }

    // the name on the workspace menu; pages without a model still show which workspace they're in
    getWorkspaceName() {
        return this.linqset?.workspaceName || this.stateName || "Current state"
    }

    // the walkthrough of the organisation's models, comparing with the project being worked in, if any
    openModelWalkthrough() {
        if (this.organisation) this.router.navigateByUrl(MODEL_WALKTHROUGH_URL(this.organisation._key, this.project?._key || this.linqset?.workspaceKey))
    }

    // the demo organisation, which everyone can see, for a walkthrough of example models
    hasDemoOrganisation() {
        return !!environment.demoOrganisationKey
    }

    openDemoWalkthrough() {
        this.router.navigateByUrl(MODEL_WALKTHROUGH_URL(environment.demoOrganisationKey))
    }

    getOrganisationModelRoute() {
        return this.organisation && SKETCH_URL(this.organisation._key, ORGANISATION_MODEL_KEY, MODEL_TYPE.MODELS, this.project && this.project._key)
    }

    getPeopleSystemsModelRoute() {
        return this.organisation && SKETCH_URL(this.organisation._key, ORGANISATION_MODEL_KEY, MODEL_TYPE.PEOPLE_AND_SYSTEMS, this.project && this.project._key)
    }

    // the analysis screen's views, as its tabs offer them
    readonly lenses = LENSES

    openLens(lens:Lens) {
        if (!this.organisation) return
        // from a model being edited, the analysis looks at that model, in its workspace
        const l = this.linqset
        if (this.isModelScreen() && l && !l.isOrganisationModel() && !l.isTutorial()) {
            const scope = this.scopeService.open(this.organisation._key)
            this.scopeService.set({...scope, workspaceKey: l.workspaceKey, modelKeys: [l.modelKey], modelFunctions: [], modelTags: []})
        }
        if (lens.key === 'overview') this.gotoInsightsUrl()
        else this.router.navigateByUrl(lens.url(this.organisation._key))
    }

    // the analysis overview, looking at the model open (if one is)
    gotoInsightsUrl() {
        if (this.linqset) {
            const u = OVERVIEW_URL(this.organisation._key) + "?workspaceKey=" + this.linqset.workspaceKey + "&modelKey=" + this.linqset.modelKey
            this.router.navigateByUrl(u)
        }else {
            this.router.navigateByUrl(OVERVIEW_URL(this.organisation._key))
        }
    }


    gotoBillingUrl() {
        this.router.navigateByUrl(BILLING_URL(this.organisation._key))
    }

    getModelUrl() {
        return this.organisation && SKETCH_URL(this.organisation._key, this.linqset.modelKey, MODEL_TYPE.PROCESS, this.linqset.workspaceKey)
    }

    goHome() {
        if (this.organisation)
            this.router.navigateByUrl(PROJECTS_URL(this.organisation._key, this.project?this.project._key:CURRENT_STATE_PROJECT_KEY))
    }

    isThisLinqsetDirty() {
        return !!this.linqset?.changes && this.linqset.isDirty()
    }

    getNumberOfChanges() {
        return this.linqset && this.linqset.changes?getNumberOfChanges(this.linqset.changes):0
    }

    getModelSaveStatus() {
        return (this.isThisLinqsetDirty())?"Save required":"Up to date"
    }

    getListOfChanges() {
        return this.linqset && this.linqset.changes?getListOfChanges(this.linqset.changes):[]
    }

    getTagGroups() {
        return (this.organisation && this.organisation.tagGroups)
    }

    getTagGroupName(tg:TagGroup) {
        if (tg.modelKeys && tg.modelKeys.length>0) {
            if (tg.modelKeys.indexOf(TAG_GROUP_ALL_MODELS) >= 0)
                return `${tg.name} - organisation`
            else return `${tg.name} - ${tg.modelKeys.length} ${tg.modelKeys.length===1?'model':'models'}`
        }else return `${tg.name} - organisation`
    }

    isLoggedIn() {
        return this.userService.isLoggedIn()
    }

    getOrganisationUrl(organisation) {
        return this.organisation && DEFAULT_URL(organisation._key)
    }

    selectOrganisation(organisation) {
        this.router.navigateByUrl(this.getOrganisationUrl(organisation))
    }

    onAdminOrganisationsClick() {
        this.router.navigateByUrl('/organisations')
    }

    handleSelectNodeEvent(event) {
        this.selectNodeEvent.emit(event)
    }

    handleFunctionEvent(event) {
        this.menuEvent.emit(event)
    }

    executeFunction(fn:FUNCTION, value?:any) {
        if (!this.functionMap[fn].disabled) this.menuEvent.emit({function:fn, value:value})
    }

    cleanOrphans() {
        this.repoService.cleanOrphans(this.organisation._key)
    }
}
